import React, { useEffect, useState, useRef } from "react";
import { Send } from "lucide-react";
import api, { resolveImg } from "../../lib/api";
import { logger } from "../../lib/logger";
import { inp } from "./shared";

const ConvoList = ({ convos, activeId, onSelect }) => (
  <div className="border border-[#262421] overflow-y-auto">
    {convos.length === 0 && <p className="p-4 text-sm text-[#8A857D]">No conversations yet.</p>}
    {convos.map((c) => (
      <button key={c.id} onClick={() => onSelect(c)} data-testid={`chat-convo-${c.id}`} className={`w-full text-start p-4 border-b border-[#262421] ${activeId === c.id ? "bg-[#1C1C1C]" : ""}`}>
        <div className="flex justify-between items-center">
          <span className="serif text-lg">{c.name}</span>
          {c.unread_admin > 0 && <span className="bg-[#C49A6C] text-[#0B0A08] text-[10px] px-2 rounded-full">{c.unread_admin}</span>}
        </div>
        <p className="text-xs text-[#8A857D]">{c.phone}</p>
      </button>
    ))}
  </div>
);

const MEDIA = {
  text: (m) => <p className="whitespace-pre-wrap break-words">{m.text}</p>,
  image: (m) => <img src={resolveImg(m.media_url)} alt="" className="max-w-full rounded" />,
  video: (m) => <video src={resolveImg(m.media_url)} controls className="max-w-full rounded" />,
  audio: (m) => <audio src={resolveImg(m.media_url)} controls />,
};

const MessageBubble = ({ m }) => (
  <div className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[75%] p-2.5 text-sm ${m.sender === "admin" ? "bg-[#C49A6C] text-[#0B0A08]" : "bg-[#1C1C1C] text-[#F2EBE1]"}`}>
      {(MEDIA[m.type] || MEDIA.text)(m)}
    </div>
  </div>
);

const useConversations = () => {
  const [convos, setConvos] = useState([]);
  useEffect(() => {
    const load = () => api.get("/chat/conversations").then((r) => setConvos(r.data)).catch((e) => logger.error("conversations load failed", e));
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);
  return convos;
};

const useThread = (active) => {
  const [msgs, setMsgs] = useState([]);
  const lastTs = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    setMsgs([]); lastTs.current = null;
    api.post(`/chat/conversations/${active.id}/read`).catch((e) => logger.error("mark read failed", e));
    let on = true;
    const poll = async () => {
      const url = `/chat/messages/${active.id}` + (lastTs.current ? `?after=${encodeURIComponent(lastTs.current)}` : "");
      let data = [];
      try { ({ data } = await api.get(url)); } catch (e) { logger.error("messages poll failed", e); return; }
      if (!on || !data.length) return;
      lastTs.current = data[data.length - 1].created_at;
      setMsgs((m) => [...m, ...data]);
    };
    poll();
    const id = setInterval(poll, 2000);
    return () => { on = false; clearInterval(id); };
  }, [active]);
  const append = (m) => { setMsgs((list) => [...list, m]); lastTs.current = m.created_at; };
  return [msgs, append];
};

export function ChatAdmin() {
  const convos = useConversations();
  const [active, setActive] = useState(null);
  const [msgs, append] = useThread(active);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs]);

  const reply = async () => {
    if (!text.trim() || !active) return;
    const body = text; setText("");
    try {
      const { data } = await api.post("/chat/message", { conversation_id: active.id, sender: "admin", type: "text", text: body });
      append(data);
    } catch (e) { logger.error("reply failed", e); setText(body); }
  };

  return (
    <div>
      <h1 className="serif text-3xl mb-8">Live Chat</h1>
      <div className="grid md:grid-cols-3 gap-4 h-[70vh]">
        <ConvoList convos={convos} activeId={active?.id} onSelect={setActive} />
        <div className="md:col-span-2 border border-[#262421] flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-[#6E685F] text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="p-4 border-b border-[#262421]"><span className="serif text-lg">{active.name}</span> <span className="text-xs text-[#8A857D]">· {active.phone}</span></div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {msgs.map((m, i) => <MessageBubble key={`${m.created_at}-${i}`} m={m} />)}
              </div>
              <div className="p-3 border-t border-[#262421] flex gap-2">
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reply()} data-testid="chat-admin-input" placeholder="Reply…" className={inp + " flex-1"} />
                <button onClick={reply} data-testid="chat-admin-send" className="bg-[#C49A6C] text-[#0B0A08] px-4 flex items-center"><Send size={18} /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
